import { useEffect, useRef, useState } from "react";
import type { ClusterSnapshot } from "../types";
import { fetchSnapshot } from "../api/client";
import { getClientSim } from "../sim/clientSim";

type Status = "connecting" | "live" | "polling" | "error" | "demo";

// Force the in-browser simulator (set at build time for the Pages deploy).
const FORCE_CLIENT_SIM = import.meta.env.VITE_USE_CLIENT_SIM === "true";

/**
 * Subscribes to the backend WebSocket stream and exposes the current cluster
 * snapshot. Priority:
 *
 *   1. VITE_USE_CLIENT_SIM=true  → run the bundled simulator in-browser.
 *   2. Try to connect to the backend WebSocket.
 *   3. If the WebSocket fails AND /api/v1/snapshot fails, fall back to the
 *      in-browser simulator automatically.
 */
export function useLiveData() {
  const [snapshot, setSnapshot] = useState<ClusterSnapshot | null>(null);
  const [status, setStatus] = useState<Status>("connecting");
  const wsRef = useRef<WebSocket | null>(null);
  const retryTimer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (FORCE_CLIENT_SIM) {
      const sim = getClientSim();
      const unsub = sim.subscribe((s) => !cancelled && setSnapshot(s));
      setStatus("demo");
      return () => {
        cancelled = true;
        unsub();
      };
    }

    // Try backend first — if it answers, stay in live/poll mode.
    let backendOk = false;
    fetchSnapshot()
      .then((s) => {
        if (cancelled) return;
        backendOk = true;
        setSnapshot(s);
      })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        if (backendOk) {
          connectWs();
        } else {
          // No backend — run the sim in-browser.
          const sim = getClientSim();
          const unsub = sim.subscribe((s) => !cancelled && setSnapshot(s));
          setStatus("demo");
          return () => unsub();
        }
      });

    function connectWs() {
      if (cancelled) return;
      setStatus("connecting");
      const base =
        import.meta.env.VITE_WS_URL ??
        (location.protocol === "https:" ? "wss://" : "ws://") + location.host;
      const ws = new WebSocket(`${base}/ws`);
      wsRef.current = ws;
      ws.onopen = () => setStatus("live");
      ws.onmessage = (ev) => {
        try {
          setSnapshot(JSON.parse(ev.data) as ClusterSnapshot);
        } catch {
          /* ignore */
        }
      };
      ws.onerror = () => setStatus("error");
      ws.onclose = () => {
        if (cancelled) return;
        setStatus("polling");
        const poll = async () => {
          if (cancelled) return;
          try {
            setSnapshot(await fetchSnapshot());
          } catch {
            /* keep trying */
          }
        };
        poll();
        const pollTimer = window.setInterval(poll, 1000);
        retryTimer.current = window.setTimeout(() => {
          window.clearInterval(pollTimer);
          connectWs();
        }, 3000);
      };
    }

    return () => {
      cancelled = true;
      wsRef.current?.close();
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    };
  }, []);

  return { snapshot, status };
}

// Re-export for App.tsx so it can dispatch demo triggers whether it's running
// against the backend or the in-browser sim.
export function useDemoTrigger(): (
  action:
    | "xid79"
    | "node-down"
    | "ib-link-down"
    | "ib-symbol-spike"
    | "gpu-mem-high"
    | "resolve-all"
) => void {
  return (action) => {
    if (FORCE_CLIENT_SIM) {
      const sim = getClientSim();
      switch (action) {
        case "xid79": sim.triggerXid79(); break;
        case "node-down": sim.triggerNodeDown("hpc-node-04"); break;
        case "ib-link-down": sim.triggerIbLinkDown(5); break;
        case "ib-symbol-spike": sim.triggerIbSymbolSpike(); break;
        case "gpu-mem-high": sim.triggerGpuMemHigh(2); break;
        case "resolve-all": sim.resolveAll(); break;
      }
      return;
    }
    // fire-and-forget POST; see api/client.ts
    import("../api/client").then((m) => m.triggerDemo(action).catch(() => {}));
  };
}
